/**
 * Interaction Service
 * Handles tracking user interactions with resources
 */

import apiClient from './api/apiClient';
import { ENDPOINTS } from './api/endpoints';

/**
 * Interaction Service Object
 * Contains methods for tracking user interactions
 */
export const interactionService = {
  /**
   * Track a resource view
   * Sends a POST request to record that the user viewed a resource
   *
   * @param {string} resourceType - Type of resource (e.g., "material", "sector")
   * @param {string} resourceId - ID/code of the resource (e.g., "MAT_7")
   * @returns {Promise<void>} - Resolves when tracking is complete
   *
   * Note: This function is designed to be fire-and-forget.
   * Errors are logged in development but never shown to users.
   */
  trackView: async (resourceType, resourceId) => {
    try {
      // Validate inputs
      if (!resourceType || !resourceId) {
        console.warn('[Interaction Service] Invalid parameters:', { resourceType, resourceId });
        return;
      }

      // Send tracking request
      await apiClient.post(ENDPOINTS.INTERACTIONS.TRACK_VIEW, {
        resource_type: resourceType,
        resource_id: resourceId,
      });

      // Log success in development
      if (process.env.NODE_ENV === 'development') {
        console.log('[Interaction Service] Tracked view:', {
          resource_type: resourceType,
          resource_id: resourceId,
        });
      }
    } catch (error) {
      // Silent error handling - log only in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Interaction Service] Failed to track view:', error.message);
        console.warn('[Interaction Service] Error details:', error.data);
        console.warn('[Interaction Service] Request payload:', {
          resource_type: resourceType,
          resource_id: resourceId,
        });
      }
      // Don't throw - this should never interrupt the user experience
    }
  },

  /**
   * Track a material view (convenience method)
   *
   * @param {number} materialId - Material ID (numeric, e.g., 47)
   * @returns {Promise<void>}
   */
  trackMaterialView: async (materialId) => {
    return interactionService.trackView('material', materialId);
  },

  /**
   * Track a sector view (convenience method)
   *
   * @param {string|number} sectorId - Sector ID
   * @returns {Promise<void>}
   */
  trackSectorView: async (sectorId) => {
    return interactionService.trackView('sector', String(sectorId));
  },
};

export default interactionService;
